import './accordion.scss';

/**
 * Class representing an Accordion component.
 */
class Accordion {
    /**
     * Create an Accordion.
     * @param {HTMLElement} element - The root element of the accordion.
     */
    constructor(element) {
        /**
         * The root accordion element.
         * @type {HTMLElement}
         */
        this.accordion = element;

        /**
         * The checkboxes controlling the accordion state.
         * @type {NodeListOf<HTMLInputElement>}
         */
        this.accordionCheckbox = this.accordion.querySelectorAll('[data-accordion="checkbox"]');

        /**
         * The buttons that toggle the accordion content.
         * @type {NodeListOf<HTMLButtonElement>}
         */
        this.accordionButton = this.accordion.querySelectorAll('[data-accordion="button"]');

        /**
         * The content panels of the accordion.
         * @type {NodeListOf<HTMLElement>}
         */
        this.accordionContent = this.accordion.querySelectorAll('[data-accordion="content"]');
    }

    /**
     * Initialize the accordion by setting up event listeners.
     */
    initialize() {
        this.setEvents();
    }

    /**
     * Set click and keyboard event listeners for accordion buttons.
     * @private
     */
    setEvents() {
        this.accordionButton.forEach(button => {
            this.openAccordionFromHash(button);

            button.addEventListener('click', () => {
                this.toggleContent(button);
                this.toggleCheckbox(button);
            });

            button.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
                    event.preventDefault();
                    this.toggleContent(button);
                    this.toggleCheckbox(button);
                }
            });
        });
    }

    /**
     * Toggle the visibility of the content associated with a button.
     * @param {HTMLButtonElement} button - The button whose content to toggle.
     */
    toggleContent(button) {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', !isExpanded);

        const panelId = button.getAttribute('aria-controls');
        const content = this.accordion.querySelector(`#${panelId}`);

        if (!content) return;

        content.hidden = isExpanded;

        if (!isExpanded) {
            button.classList.add('accordion__item-button--is-active');
            content.classList.add('accordion__item-content--is-active');
        } else {
            button.classList.remove('accordion__item-button--is-active');
            content.classList.remove('accordion__item-content--is-active');
        }
    }

    /**
     * Toggle the state of the checkbox associated with a button.
     * @param {HTMLButtonElement} button - The button whose checkbox to toggle.
     */
    toggleCheckbox(button) {
        this.accordionCheckbox.forEach(checkbox => {
            if (button.closest('.accordion__item')?.contains(checkbox)) {
                checkbox.checked = !checkbox.checked;
            }
        });
    }

    /**
     * Open the accordion item if it matches the current URL hash.
     * @param {HTMLButtonElement} button - The button to check against the hash.
     */
    openAccordionFromHash(button) {
        const hash = window.location.hash;
        if (!hash) return;

        const targetHash = hash.slice(1);

        if (button.id === targetHash) {
            const panelId = button.getAttribute('aria-controls');
            const content = this.accordion.querySelector(`#${panelId}`);

            this.accordionCheckbox.forEach(checkbox => {
                if (button.closest('.accordion__item')?.contains(checkbox)) {
                    checkbox.checked = true;
                }
            });

            if (content && button.getAttribute('aria-expanded') !== 'true') {
                button.setAttribute('aria-expanded', true);
                button.classList.add('accordion__item-button--is-active');
                content.hidden = false;
                content.classList.add('accordion__item-content--is-active');
            }

            button.focus();
            button.scrollIntoView({behavior: "smooth", block: "start"});
        }
    }
}

export {Accordion};

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$accordion = $context.querySelectorAll('[data-accordion="root"]');

        $$accordion.forEach($accordionEl => {
            const accordion = new Accordion($accordionEl);
            accordion.initialize();
        });
    }
});
